/**
 * chat/chatUtils.js — pure helpers for the ticket-chat redesign.
 * Kept side-effect-free so both admin and customer surfaces can share.
 */
import { AUTHOR_SERIES_GAP_MINUTES } from './chatTokens';

// ── Day / time formatters ─────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** Format the inline meta timestamp inside a bubble.
 *  Today → HH:MM   |   otherwise → DD.MM HH:MM */
export const formatBubbleTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.getDate() === now.getDate()
    && d.getMonth() === now.getMonth()
    && d.getFullYear() === now.getFullYear();
  if (sameDay) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Human day key: 'Today', 'Yesterday', 'Monday'..'Sunday' if within
 *  the current week, else 'DD.MM.YYYY'. Used by DateDivider. */
export const formatDayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const midnight = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const diffDays = Math.round((midnight(now) - midnight(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) {
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** True iff two ISO timestamps fall on the same calendar day. */
export const sameCalendarDay = (aIso, bIso) => {
  if (!aIso || !bIso) return false;
  const a = new Date(aIso); const b = new Date(bIso);
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
};

// ── Author-series compression (spec §66-70) ───────────────────────

/**
 * Should the author label be rendered above the current message?
 *
 * Rule (option 2 chosen by product):
 *   YES if this is the very first message, OR
 *          the previous message was from a different author, OR
 *          the gap since the previous message is > 5 minutes.
 */
export const shouldShowAuthorLabel = (prev, curr) => {
  if (!curr) return false;
  if (!prev) return true;
  const authorKey = (m) => (
    m?.sender_user_id
    ?? m?.sender_id
    ?? `${m?.sender_type || ''}:${m?.sender_name || ''}`
  );
  if (authorKey(prev) !== authorKey(curr)) return true;
  const pt = new Date(prev.created_at || 0).getTime();
  const ct = new Date(curr.created_at || 0).getTime();
  if (!pt || !ct) return true;
  return (ct - pt) > AUTHOR_SERIES_GAP_MINUTES * 60_000;
};

// ── Delivery / read status derivation ─────────────────────────────

/**
 * Map a message to a bubble read-state:
 *   'sent'      — sent, not delivered (single grey ✓)
 *   'delivered' — reached the other side but unread (double grey ✓✓)
 *   'read'      — read by recipient (double green ✓✓)
 *
 * We accept several field shapes for backwards compat with the
 * various API paths in the codebase.
 */
export const deriveReadStatus = (msg) => {
  if (!msg) return 'sent';
  // Highest-confidence "read" signal
  if (msg.read_by_recipient || msg.readByRecipient || msg.read || msg.is_read) return 'read';
  // Delivered signal: message persisted server-side (has id + created_at)
  // and not a client-only optimistic row (id starts with 'tmp-').
  const persisted = msg.id && !String(msg.id).startsWith('tmp-') && msg.created_at;
  return persisted ? 'delivered' : 'sent';
};
